import type { Ref } from 'vue'

/**
 * Flips to true the first time `target` scrolls into view, then stops observing.
 * Users who prefer reduced motion get true immediately so nothing is gated on scrolling.
 */
export function useRevealOnce(target: Ref<HTMLElement | null | undefined>, options: IntersectionObserverInit = { threshold: 0.2, rootMargin: '0px 0px -6% 0px' }) {
  const revealed = ref(false)
  const reducedMotion = ref(false)
  let observer: IntersectionObserver | null = null

  onMounted(() => {
    reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion.value || !target.value) {
      revealed.value = true
      return
    }

    observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        revealed.value = true
        observer?.disconnect()
        observer = null
      }
    }, options)
    observer.observe(target.value)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
  })

  return { revealed, reducedMotion }
}
